import { useQuery } from "@apollo/client"
import { ReactNode, useState } from "react"
import { useForm } from "react-hook-form"
import { gql } from "~/__generated__"
import { UserRole } from "~/__generated__/graphql"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/errors"
import { Form, FormField } from "~/ui/form"
import { InfiniteLoadMore } from "~/ui/infinite-load-more"
import { SearchInput } from "~/ui/search-input"
import Text from "~/ui/typography"
import { columns } from "./columns"
import { DataTable } from "./data-table"

export const usersQuery = gql(/* GraphQL */ `
  query Users($first: Int = 20, $after: String, $filters: UsersFilterInput) {
    users(first: $first, after: $after, filters: $filters) {
      edges {
        node {
          id
          handle
          fullName
          email
          createdAt
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`)

type FormData = {
  search: string
}

interface UsersTableProps {
  title: string
  description: string
  userRole: UserRole
  placeholder?: string
  actionButton?: ReactNode
}

export function UsersTable({
  title,
  description,
  userRole,
  placeholder = "Search Users",
  actionButton,
}: UsersTableProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const form = useForm<FormData>({
    defaultValues: {
      search: "",
    },
  })

  const { data, fetchMore, error, loading } = useQuery(usersQuery, {
    variables: {
      first: 20,
      after: null,
      filters: {
        search: searchTerm || undefined,
        userRoles: [userRole],
      },
    },
    notifyOnNetworkStatusChange: true,
  })

  if (error) {
    return <GraphqlError error={error} />
  }

  const users = data?.users.edges.map((edge) => edge.node) ?? []
  const pageInfo = data?.users.pageInfo

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
  }

  const clearSearchAndFilters = () => {
    form.reset()
    setSearchTerm("")
  }

  const handleLoadMore = () => {
    if (pageInfo?.hasNextPage) {
      fetchMore({
        variables: {
          after: pageInfo.endCursor,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult
          return {
            users: {
              ...fetchMoreResult.users,
              edges: [...previousResult.users.edges, ...fetchMoreResult.users.edges],
            },
          }
        },
      })
    }
  }

  return (
    <TablePageLayout>
      <div
        className="mb-6 flex items-center justify-between"
        data-testid="users-search-and-filters"
      >
        <Form {...form}>
          <form className="flex w-full flex-col">
            <div className="flex w-full items-center justify-between">
              <div>
                <Text as="h1" variant="heading3">
                  {title}
                </Text>
                <p className="mt-1 text-sm text-gray-500">{description}</p>
              </div>

              <div className="flex items-center gap-2">
                <FormField
                  control={form.control}
                  name="search"
                  render={({ field }) => (
                    <SearchInput
                      placeholder={placeholder}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e)
                        handleSearchChange(e.target.value)
                      }}
                    />
                  )}
                />
                {actionButton}
              </div>
            </div>
          </form>
        </Form>
      </div>

      <div className="mb-6 h-px bg-gray-200"></div>

      {users.length > 0 && (
        <>
          <DataTable columns={columns} data={users} />
          <InfiniteLoadMore
            onEndReached={handleLoadMore}
            canLoadMore={pageInfo?.hasNextPage ?? false}
            loadingText="Loading more users..."
            loading={loading}
          />
        </>
      )}

      {!loading && !error && users.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <h3 className="text-lg font-medium text-gray-900">No Users Found</h3>
          <p className="mt-2 text-sm text-gray-500">Clear your search results to view all users</p>
          <Button variant="outline" onClick={clearSearchAndFilters} className="mt-4">
            Clear Search
          </Button>
        </div>
      )}
    </TablePageLayout>
  )
}
